import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { WizardData } from "../index";
import { useCreateCarForSale } from "../../../services";
import { uploadFiles, uploadFile as uploadSingleFile } from "../../../services/uploads";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import EditIcon from "@mui/icons-material/Edit";
import { GetAllCarsForSaleDocument, GetUserListingsDocument } from "../../../generated/graphql";
import { resolveListingTitle } from "../../../utils";
import { compressImage, compressImages } from "../../../utils/imageCompression";
import { normalizeMake, normalizeModel } from "../../../services/vehicleCatalog";
import { stringifyListingVideoEntries } from "../../../utils/listingMedia";
import { formatVideoDuration } from "../../../utils/videoValidation";

interface ReviewStepProps {
  data: WizardData;
  onBack: () => void;
  onEdit: (step: number) => void;
  darkMode: boolean;
  userId: number;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  data,
  onBack,
  onEdit,
  darkMode,
  userId,
  isSubmitting,
  setIsSubmitting,
}) => {
  const navigate = useNavigate();
  const [createCarForSale] = useCreateCarForSale();

  const photoPreviewUrls = useMemo(
    () => data.vehicleImages.slice(0, 5).map((file) => URL.createObjectURL(file)),
    [data.vehicleImages]
  );
  const videoPreviewUrls = useMemo(
    () => data.vehicleVideos.map((item) => URL.createObjectURL(item.file)),
    [data.vehicleVideos]
  );

  useEffect(() => {
    return () => {
      for (const url of photoPreviewUrls) URL.revokeObjectURL(url);
      for (const url of videoPreviewUrls) URL.revokeObjectURL(url);
    };
  }, [photoPreviewUrls, videoPreviewUrls]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Validate userId exists and is valid
      if (!userId || userId <= 0) {
        throw new Error("Invalid user ID. Please log out and log back in.");
      }

      console.log("Using userId:", userId);
      // Compress vehicle images before upload
      console.log("Compressing images...");
      const compressedImages = await compressImages(data.vehicleImages);
      console.log("Images compressed, uploading to S3 via presigned URLs...");

      // Upload vehicle images (server issues presigned URLs; no AWS creds here)
      const imageKeys = await uploadFiles(compressedImages, "vehicle");

      // Upload optional videos (no compression; MIME/size validated on Photos step)
      const videoFiles = data.vehicleVideos.map((v) => v.file);
      const videoKeys =
        videoFiles.length > 0 ? await uploadFiles(videoFiles, "video") : [];
      const videoEntries = videoKeys.map((key, i) => ({
        key,
        contentType: data.vehicleVideos[i].contentType,
        durationSeconds: data.vehicleVideos[i].durationSeconds,
        bytes: data.vehicleVideos[i].file.size,
      }));

      // Prefer the key already verified on the Photos step. Re-upload only if
      // the seller somehow has a file without a key (should be rare).
      let titleImageKey = data.titleImageKey;
      if (!titleImageKey && data.titleImage) {
        const titleFile = await compressImage(data.titleImage);
        titleImageKey = await uploadSingleFile(titleFile, "title");
      }
      const hasVerifiedTitle =
        !!titleImageKey && data.titleVerifyStatus === "passed";

      // Helper function to convert empty strings to null
      const toNullIfEmpty = (value: string | null | undefined): string | null => {
        if (value === null || value === undefined || value.trim() === '') {
          return null;
        }
        return value;
      };

      // Create the listing - now using make/model as strings directly!
      const variables = {
          sellerId: userId,
          year: data.year!,
          make: data.make.trim(),
          model: data.model.trim(),
          mileage: data.mileage ? parseInt(data.mileage.replace(/[^0-9]/g, '')) || null : null,
          price: data.price.replace(/[$,]/g, ''),
          description: data.description.trim(),
          imageLoc: JSON.stringify(imageKeys),
          videoLoc: stringifyListingVideoEntries(videoEntries),
          latitude: data.location!.latitude.toString(),
          longitude: data.location!.longitude.toString(),
          locationAddress: data.location!.address || null,
          vin: toNullIfEmpty(data.vin),
          trim: toNullIfEmpty(data.trim),
          transmission: toNullIfEmpty(data.transmission),
          fuelType: toNullIfEmpty(data.fuelType),
          engine: toNullIfEmpty(data.engine),
          driveType: toNullIfEmpty(data.driveType),
          bodyStyle: toNullIfEmpty(data.bodyStyle),
          exteriorColor: toNullIfEmpty(data.exteriorColor),
          interiorColor: toNullIfEmpty(data.interiorColor),
          titleStatus: data.titleStatus || "Clean",
          titleImageKey: toNullIfEmpty(titleImageKey),
          // Server re-stamps this from the Textract verify cache; client hint only.
          hasVerifiedTitle,
          catalogSourceId: null,
          unmatchedCatalog: false,
          catalogMatchConfidence: null,
          catalogMatchStage: null,
          normalizedMake: normalizeMake(data.make) || null,
          normalizedModel: normalizeModel(data.model) || null,
          identityProvider: data.identityProvider,
          identityProviderTier: data.identityProviderTier,
          identityStyleKey: data.identityStyleKey,
          identityVersion: data.identityVersion,
          identityConfidenceRaw: data.identityConfidenceRaw,
          identityConfidenceScore:
            data.identityConfidenceScore != null
              ? data.identityConfidenceScore.toString()
              : null,
          identityDecodedAt:
            (data.identitySnapshot as { provider?: { decodedAt?: string } } | null)
              ?.provider?.decodedAt || null,
          identityDisplayTitle: data.identityDisplayTitle,
          // Match imageLoc: Graphile JSON columns accept a JSON string reliably.
          identitySnapshot:
            data.identitySnapshot == null
              ? null
              : typeof data.identitySnapshot === "string"
                ? data.identitySnapshot
                : JSON.stringify(data.identitySnapshot),
        };

      console.log("[vin-identity]", {
        provider: data.identityProvider,
        tier: data.identityProviderTier,
        displayTitle: data.identityDisplayTitle,
        year: data.year,
        make: data.make,
        model: data.model,
        trim: data.trim,
      });

      // Validate data before sending
      if (!variables.make || !variables.model || !variables.year) {
        throw new Error("Missing required vehicle information");
      }

      if (!variables.price || isNaN(parseFloat(variables.price.replace(/[$,]/g, '')))) {
        throw new Error(`Invalid price format: "${variables.price}"`);
      }

      if (variables.mileage !== null && (variables.mileage < 0 || variables.mileage > 1000000)) {
        throw new Error(`Invalid mileage value: ${variables.mileage}`);
      }

      console.log("Creating listing with variables:", JSON.stringify(variables, null, 2));

      const result = await createCarForSale({
        variables,
        refetchQueries: [
          { query: GetAllCarsForSaleDocument },
          { query: GetUserListingsDocument, variables: { sellerId: userId, active: true } },
          { query: GetUserListingsDocument, variables: { sellerId: userId, active: false } },
        ],
      });

      console.log("GraphQL result:", JSON.stringify(result, null, 2));

      if (result.data?.createCarsForSale?.carsForSale) {
        navigate("/");
      } else {
        console.error("No data returned:", result);
        throw new Error("Failed to create listing: No data returned from server");
      }
    } catch (error: any) {
      console.error("Error creating listing:", error);
      console.error("Error details:", JSON.stringify(error, null, 2));
      
      // Handle Apollo Client errors
      let errorMessage = "Failed to create listing. Please try again.";
      
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // GraphQL errors
        const graphQLError = error.graphQLErrors[0].message;
        
        // Check for foreign key constraint violation
        if (graphQLError.includes("foreign key constraint") && graphQLError.includes("seller_id")) {
          errorMessage = "Your user account is not valid. Please log out and log back in, or contact support if the issue persists.";
        } else {
          errorMessage = error.graphQLErrors.map((e: any) => e.message).join('; ');
        }
        console.error("GraphQL errors:", error.graphQLErrors);
      } else if (error.networkError) {
        // Our Express middleware (email-verification, VIN integrity, duplicate
        // VIN) rejects before PostGraphile with an HTTP 4xx whose body is
        // { errors: [{ message, extensions: { code } }] }. Apollo surfaces that
        // as a networkError with a `.result`. Prefer that friendly message
        // (e.g. "A listing for this VIN already exists.") over a generic one.
        const resultErrors =
          (error.networkError as any)?.result?.errors;
        if (Array.isArray(resultErrors) && resultErrors.length > 0) {
          errorMessage = resultErrors.map((e: any) => e.message).join("; ");
        } else {
          errorMessage = `Network error: ${error.networkError.message || 'Failed to connect to server'}`;
        }
        console.error("Network error:", error.networkError);
      } else if (error.message) {
        // Regular error with message
        errorMessage = error.message;
      } else {
        // Unknown error format
        errorMessage = `Unknown error: ${JSON.stringify(error)}`;
      }
      
      alert(`Failed to create listing: ${errorMessage}`);
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price: string) => {
    const num = parseFloat(price);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(num);
  };

  return (
    <div className="step-container">
      <h2 className="step-title" data-testid="wizard-review-title">Review Your Listing</h2>
      <p className="step-subtitle">
        Please review all details before publishing
      </p>

      {/* Trust badges preview */}
      <div className="review-badges">
        <div className="trust-badge vin-verified">
          <CheckCircleIcon style={{ fontSize: "16px" }} />
          VIN Verified
        </div>
        {data.titleVerifyStatus === "passed" && (
          <div className="trust-badge title-verified">
            <VerifiedIcon style={{ fontSize: "16px" }} />
            Title on File
          </div>
        )}
      </div>

      {/* Vehicle Info */}
      <div className="review-section" style={{ marginTop: "24px" }}>
        <div className="review-section-title">
          Vehicle Information
          <button className="review-edit-btn" onClick={() => onEdit(1)}>
            <EditIcon style={{ fontSize: "14px", marginRight: "4px" }} />
            Edit
          </button>
        </div>
        <div style={{ 
          fontSize: "20px", 
          fontWeight: "600", 
          marginBottom: "16px",
          color: darkMode ? "#fff" : "#333"
        }}>
          {resolveListingTitle({
            identityDisplayTitle: data.identityDisplayTitle,
            year: data.year,
            make: data.make,
            model: data.model,
            trim: data.trim,
          })}
        </div>
        <div className="review-grid">
          <div className="review-item">
            <span className="review-item-label">VIN</span>
            <span className="review-item-value" style={{ fontFamily: "monospace" }}>
              {data.vin}
            </span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Mileage</span>
            <span className="review-item-value">
              {parseInt(data.mileage).toLocaleString()} miles
            </span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Body Style</span>
            <span className="review-item-value">{data.bodyStyle || "—"}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Drive Type</span>
            <span className="review-item-value">{data.driveType || "—"}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Transmission</span>
            <span className="review-item-value">{data.transmission || "—"}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Fuel Type</span>
            <span className="review-item-value">{data.fuelType || "—"}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Engine</span>
            <span className="review-item-value">{data.engine || "—"}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Title Status</span>
            <span className="review-item-value">{data.titleStatus}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Exterior Color</span>
            <span className="review-item-value">{data.exteriorColor}</span>
          </div>
          <div className="review-item">
            <span className="review-item-label">Interior Color</span>
            <span className="review-item-value">{data.interiorColor}</span>
          </div>
        </div>
      </div>

      {/* Photos */}
      <div className="review-section">
        <div className="review-section-title">
          Photos ({data.vehicleImages.length})
          <button className="review-edit-btn" onClick={() => onEdit(3)}>
            <EditIcon style={{ fontSize: "14px", marginRight: "4px" }} />
            Edit
          </button>
        </div>
        <div className="review-photos">
          {photoPreviewUrls.map((url, index) => (
            <div key={index} style={{ position: "relative", display: "inline-block" }}>
              <img
                src={url}
                alt={`${index + 1}`}
                className="review-photo-thumb"
                style={index === 0 ? { border: "2px solid var(--color-primary)", borderRadius: "4px" } : undefined}
              />
              {index === 0 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "2px",
                    left: "2px",
                    background: "var(--color-primary)",
                    color: "white",
                    fontSize: "9px",
                    padding: "1px 4px",
                    borderRadius: "3px",
                    fontWeight: 500,
                  }}
                >
                  Main
                </div>
              )}
            </div>
          ))}
          {data.vehicleImages.length > 5 && (
            <div
              style={{
                width: "80px",
                height: "60px",
                borderRadius: "4px",
                background: darkMode ? "#333" : "#e0e0e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                color: darkMode ? "#aaa" : "#666",
              }}
            >
              +{data.vehicleImages.length - 5} more
            </div>
          )}
        </div>
      </div>

      {/* Videos */}
      <div className="review-section">
        <div className="review-section-title">
          Videos ({data.vehicleVideos.length})
          <button className="review-edit-btn" onClick={() => onEdit(3)}>
            <EditIcon style={{ fontSize: "14px", marginRight: "4px" }} />
            Edit
          </button>
        </div>
        {data.vehicleVideos.length === 0 ? (
          <p
            style={{
              margin: 0,
              fontSize: "13px",
              color: darkMode ? "#888" : "#666",
            }}
          >
            No videos added (optional)
          </p>
        ) : (
          <div className="review-photos">
            {data.vehicleVideos.map((item, index) => (
              <div
                key={`${item.file.name}-${index}`}
                style={{ position: "relative", display: "inline-block" }}
              >
                <video
                  src={videoPreviewUrls[index]}
                  muted
                  playsInline
                  preload="metadata"
                  className="review-photo-thumb"
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "2px",
                    left: "2px",
                    background: "rgba(0,0,0,0.7)",
                    color: "white",
                    fontSize: "9px",
                    padding: "1px 4px",
                    borderRadius: "3px",
                    fontWeight: 500,
                  }}
                >
                  {formatVideoDuration(item.durationSeconds) || "Video"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pricing & Location */}
      <div className="review-section">
        <div className="review-section-title">
          Pricing & Location
          <button className="review-edit-btn" onClick={() => onEdit(4)}>
            <EditIcon style={{ fontSize: "14px", marginRight: "4px" }} />
            Edit
          </button>
        </div>
        <div style={{ 
          fontSize: "24px", 
          fontWeight: "700", 
          color: "var(--color-primary)",
          marginBottom: "12px"
        }}>
          {formatPrice(data.price)}
        </div>
        <div className="review-grid">
          <div className="review-item">
            <span className="review-item-label">Location</span>
            <span className="review-item-value">{data.location?.address}</span>
          </div>
        </div>
        <div style={{ marginTop: "12px" }}>
          <span className="review-item-label">Description</span>
          <p style={{ 
            marginTop: "4px", 
            fontSize: "14px",
            color: darkMode ? "#ddd" : "#333",
            lineHeight: "1.5"
          }}>
            {data.description}
          </p>
        </div>
      </div>

      <div className="wizard-buttons">
        <button
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </button>
        <button
          className="wizard-btn wizard-btn-primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{ minWidth: "180px" }}
        >
          {isSubmitting ? (
            <span className="wizard-loading">
              <span className="spinner" />
              Publishing...
            </span>
          ) : (
            "Publish Listing"
          )}
        </button>
      </div>
    </div>
  );
};

export default ReviewStep;
